import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { ToySprite } from '../objects/ToySprite';
import { StyledButton } from '../objects/StyledButton';
import { NavigationBar } from '../objects/NavigationBar';
import { ResultOverlay } from '../objects/ResultOverlay';
import { transitionManager } from '../systems/TransitionManager';
import { eventTracker } from '../analytics/EventTracker';
import { AccuracySystem } from '../systems/AccuracySystem';
import { GrabSystem } from '../systems/GrabSystem';
import { SpawnSystem } from '../systems/SpawnSystem';
import { CollectionSystem } from '../systems/CollectionSystem';
import { soundManager } from '../systems/SoundManager';
import { TOYS, getVisualForToy, getRarityGlow } from '../config/toys';
import { GAME_CONFIG } from '../config/gameConfig';
import { detectPlatform } from '../utils/platform';
import type { ToyConfig } from '../types';

type InternalState = 'IDLE' | 'MOVING' | 'AIMING' | 'DROP' | 'GRABBING' | 'LIFTING' | 'SUSPENSE' | 'RESULT';

export class GameScene extends Phaser.Scene {
  private crane!: Phaser.GameObjects.Container;
  private claw!: Phaser.GameObjects.Container;
  private clawHousing!: Phaser.GameObjects.Graphics;
  private prongLeft!: Phaser.GameObjects.Graphics;
  private prongRight!: Phaser.GameObjects.Graphics;
  private grabbedToy: ToySprite | null = null;
  private cable!: Phaser.GameObjects.Rectangle;
  private dropButton!: StyledButton;
  private instructionText!: Phaser.GameObjects.Text;
  private resultPanel: Phaser.GameObjects.Container | null = null;
  private toySprites: ToySprite[] = [];
  private hudBadgeText!: Phaser.GameObjects.Text;
  private targetIndicator!: Phaser.GameObjects.Graphics;

  private state: InternalState = 'IDLE';
  private craneX = 640;
  private readonly minX = 260;
  private readonly maxX = 1020;
  private readonly topY = 140;
  private readonly dropY = 480;
  private readonly autoSpeed = 180;

  private accuracySystem!: AccuracySystem;
  private spawnSystem!: SpawnSystem;
  private grabSystem!: GrabSystem;
  private collectionSystem!: CollectionSystem;
  private currentToy!: ToyConfig;
  private currentTargetX = 640;
  private platform: 'desktop' | 'mobile' = 'desktop';
  private autoDirection = 1;
  private experimentVersion = 'B';

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: { experimentVersion?: string }): void {
    this.experimentVersion = data.experimentVersion ?? 'B';
  }

  create(): void {
    this.platform = detectPlatform();
    this.spawnSystem = new SpawnSystem(TOYS);
    this.grabSystem = new GrabSystem(GAME_CONFIG.nearMissRate, this.experimentVersion);
    this.collectionSystem = new CollectionSystem();
    this.currentToy = this.spawnSystem.spawn();

    this.accuracySystem = new AccuracySystem(640, 380);

    this.addBackground();
    this.addNavigation();
    this.addMachine();
    this.addTargetIndicator();
    this.addToys();
    this.createCrane();
    this.createHUD();
    this.createDropButton();
    this.createInstructions();
    this.setupInput();

    eventTracker.track('machine_enter', {
      toy: this.currentToy.id,
      rarity: this.currentToy.rarity,
    });
  }

  private addBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }

    const circles = [
      { x: 120, y: 120, r: 80, color: 0xffdad8, alpha: 0.4 },
      { x: 1160, y: 200, r: 120, color: 0x88f2e0, alpha: 0.25 },
      { x: 100, y: 600, r: 140, color: 0xffe173, alpha: 0.2 },
      { x: 1180, y: 650, r: 90, color: 0xff7e7e, alpha: 0.25 },
    ];
    circles.forEach((c) => {
      graphics.fillStyle(c.color, c.alpha);
      graphics.fillCircle(c.x, c.y, c.r);
    });
  }

  private addNavigation(): void {
    new NavigationBar(this, 0, 35, 'play', (key) => this.handleNavigation(key));
  }

  private addMachine(): void {
    const m = DESIGN.machine;
    const cx = 640;
    const cy = 400;

    const shadow = this.add.rectangle(cx + 12, cy + 12, m.width + 24, m.height + 24, 0x000000, 0.12);
    shadow.setOrigin(0.5);

    const casing = this.add.graphics();
    casing.fillStyle(parseInt(DESIGN.colors.primary.replace('#', '0x'), 16), 1);
    casing.fillRoundedRect(cx - m.width / 2 - 12, cy - m.height / 2 - 12, m.width + 24, m.height + 24, 40);
    casing.fillStyle(parseInt(DESIGN.colors.primaryContainer.replace('#', '0x'), 16), 1);
    casing.fillRoundedRect(cx - m.width / 2 - 6, cy - m.height / 2 - 6, m.width + 12, m.height + 12, 36);

    const cabinet = this.add.graphics();
    cabinet.fillStyle(parseInt(DESIGN.colors.surface.replace('#', '0x'), 16), 1);
    cabinet.fillRoundedRect(cx - m.width / 2 + 12, cy - m.height / 2 + 12, m.width - 24, m.height - 140, 28);
    cabinet.fillStyle(0x000000, 0.05);
    cabinet.fillRoundedRect(cx - m.width / 2 + 12, cy - m.height / 2 + 12, m.width - 24, m.height - 140, 28);

    const rail = this.add.rectangle(cx, cy - m.height / 2 + 42, m.width - 60, 10, 0xaaaaaa, 0.3);
    rail.setOrigin(0.5);

    const pit = this.add.graphics();
    pit.fillStyle(parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16), 1);
    pit.fillRoundedRect(cx - m.width / 2 + 30, cy + m.height / 2 - 140, m.width - 60, 80, 20);

    const legendX = cx - m.width / 2 + 30;
    const legendY = cy - m.height / 2 + 60;
    this.add.text(legendX + 10, legendY, 'Common', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.common,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add.circle(legendX, legendY, 4, parseInt(DESIGN.colors.common.replace('#', '0x'), 16));

    this.add.text(legendX + 90, legendY, 'Rare', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.rare,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add.circle(legendX + 80, legendY, 4, parseInt(DESIGN.colors.rare.replace('#', '0x'), 16));
  }

  private addTargetIndicator(): void {
    this.targetIndicator = this.add.graphics();
  }

  private updateTargetIndicator(): void {
    this.targetIndicator.clear();
    this.targetIndicator.fillStyle(0xffffff, 0.4);
    this.targetIndicator.fillCircle(this.currentTargetX, 552, 18);
    this.targetIndicator.lineStyle(2, 0xff7e7e, 0.8);
    this.targetIndicator.strokeCircle(this.currentTargetX, 552, 18);
  }

  private addToys(): void {
    this.toySprites.forEach((s) => s.destroy());
    this.toySprites = [];

    const toyPositions = [460, 580, 700, 820];
    const targetIndex = Math.floor(Math.random() * toyPositions.length);
    this.currentTargetX = toyPositions[targetIndex];

    for (let i = 0; i < toyPositions.length; i++) {
      const x = toyPositions[i];
      const isTarget = i === targetIndex;
      const toy = isTarget ? this.currentToy : this.spawnSystem.spawn();
      const visual = getVisualForToy(toy.id);
      const sprite = new ToySprite(this, x, 520, visual, 72);

      if (isTarget) {
        sprite.setGlow(getRarityGlow(toy.rarity), 16);
      }
      this.toySprites.push(sprite);
    }

    this.updateTargetIndicator();
    this.accuracySystem.updateTarget(this.currentTargetX);
  }

  private createCrane(): void {
    this.cable = this.add.rectangle(0, 0, 6, 220, 0xaaaaaa);
    this.cable.setOrigin(0.5, 0);
    this.cable.setPosition(0, -80);

    this.claw = this.createClawAssembly();

    this.crane = this.add.container(this.craneX, this.topY);
    this.crane.add([this.cable, this.claw]);
  }

  private createClawAssembly(): Phaser.GameObjects.Container {
    const container = this.add.container(0, 130);

    this.clawHousing = this.add.graphics();
    const primary = parseInt(DESIGN.colors.primary.replace('#', '0x'), 16);
    const primaryContainer = parseInt(DESIGN.colors.primaryContainer.replace('#', '0x'), 16);
    this.clawHousing.fillStyle(primary, 1);
    this.clawHousing.fillRoundedRect(-28, -20, 56, 40, 12);
    this.clawHousing.fillStyle(primaryContainer, 1);
    this.clawHousing.fillRoundedRect(-22, -14, 44, 20, 8);
    container.add(this.clawHousing);

    const shine = this.add.graphics();
    shine.fillStyle(0xffffff, 0.4);
    shine.fillRoundedRect(-20, -16, 36, 10, 5);
    container.add(shine);

    this.prongLeft = this.add.graphics();
    this.drawProng(this.prongLeft, primary, -1, 0);
    container.add(this.prongLeft);

    this.prongRight = this.add.graphics();
    this.drawProng(this.prongRight, primary, 1, 0);
    container.add(this.prongRight);

    return container;
  }

  private drawProng(graphics: Phaser.GameObjects.Graphics, color: number, side: -1 | 1, openAmount: number): void {
    graphics.clear();
    graphics.fillStyle(color, 1);
    // side * 12 is base x, side * 22 is tip x offset based on openness
    const baseX = side * 12;
    const tipX = side * (22 + openAmount * 14);
    const width = 10;
    const height = 44;
    const round = 5;
    // Draw a rounded rectangle rotated slightly by open amount
    graphics.save();
    graphics.translateCanvas(baseX + (tipX - baseX) / 2, 12 + height / 2);
    graphics.rotateCanvas(side * openAmount * 0.3);
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, round);
    graphics.restore();
  }

  private setClawOpen(openAmount: number): void {
    const primary = parseInt(DESIGN.colors.primary.replace('#', '0x'), 16);
    this.drawProng(this.prongLeft, primary, -1, openAmount);
    this.drawProng(this.prongRight, primary, 1, openAmount);
  }

  private openClaw(duration = 200): void {
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration,
      onUpdate: (tween) => {
        this.setClawOpen(tween.getValue() ?? 0);
      },
    });
  }

  private closeClaw(duration = 200): void {
    this.tweens.addCounter({
      from: 1,
      to: 0,
      duration,
      onUpdate: (tween) => {
        this.setClawOpen(tween.getValue() ?? 0);
      },
    });
  }

  private createHUD(): void {
    this.hudBadgeText = this.add.text(240, 35, `${this.collectionSystem.count()} Toys`, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  private createDropButton(): void {
    const label = this.platform === 'desktop' ? 'DROP' : 'TAP TO DROP';
    this.dropButton = new StyledButton(this, 640, 660, label, {
      width: 240,
      height: 64,
      bgColor: parseInt(DESIGN.colors.primary.replace('#', '0x'), 16),
      shadowColor: parseInt(DESIGN.colors.primaryDark.replace('#', '0x'), 16),
      fontSize: '26px',
      onClick: () => this.onDrop(),
    });
  }

  private createInstructions(): void {
    const text = this.platform === 'desktop'
      ? 'Click inside the machine to aim • Click DROP to grab!'
      : 'Tap anywhere when the claw is ready!';
    this.instructionText = this.add.text(640, 95, text, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurface,
      backgroundColor: '#ffffffcc',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5);
    this.instructionText.setDepth(10);
  }

  private setupInput(): void {
    // Cabinet zone: click/tap anywhere inside the machine to aim
    const cabinetZone = this.add.rectangle(640, 400, 520, 560, 0x000000, 0).setInteractive();
    cabinetZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.state === 'IDLE' || this.state === 'MOVING') {
        this.moveCraneTo(pointer.x);
      }
    });

    // Global tap/click to drop when the claw is ready
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.platform === 'mobile' && (this.state === 'IDLE' || this.state === 'MOVING')) {
        this.onDrop();
      } else if (this.platform === 'desktop' && (this.state === 'IDLE' || this.state === 'MOVING')) {
        // If user clicked inside the cabinet but not directly on a toy, we already aim.
        // Additionally, if they clicked below the toys (near the chute / drop zone), trigger drop.
        if (pointer.y > 560) {
          this.onDrop();
        }
      }
    });

    this.input.keyboard?.on('keydown-LEFT', () => this.nudgeCrane(-10));
    this.input.keyboard?.on('keydown-RIGHT', () => this.nudgeCrane(10));
    this.input.keyboard?.on('keydown-SPACE', () => this.onDrop());
    this.input.keyboard?.on('keydown-ENTER', () => this.onDrop());

    if (this.platform === 'mobile') {
      this.state = 'MOVING';
    }
  }

  private nudgeCrane(delta: number): void {
    if (this.state !== 'IDLE' && this.state !== 'MOVING') return;
    this.moveCraneTo(this.craneX + delta);
  }

  private moveCraneTo(targetX: number): void {
    const clamped = Phaser.Math.Clamp(targetX, this.minX, this.maxX);
    this.state = 'MOVING';
    this.tweens.add({
      targets: { x: this.craneX },
      x: clamped,
      duration: 200,
      ease: 'Sine.easeOut',
      onUpdate: (tween: Phaser.Tweens.Tween) => {
        const obj = tween.targets[0] as { x: number };
        this.craneX = obj.x;
        this.crane.setX(this.craneX);
      },
    });
  }

  update(_time: number, delta: number): void {
    if (this.platform === 'mobile' && (this.state === 'IDLE' || this.state === 'MOVING')) {
      this.craneX += this.autoDirection * this.autoSpeed * (delta / 1000);
      if (this.craneX >= this.maxX) {
        this.craneX = this.maxX;
        this.autoDirection = -1;
      } else if (this.craneX <= this.minX) {
        this.craneX = this.minX;
        this.autoDirection = 1;
      }
      this.crane.setX(this.craneX);
      this.state = 'MOVING';
    }
  }

  private onDrop(): void {
    if (this.state !== 'IDLE' && this.state !== 'MOVING') return;

    soundManager.play('clawMove');
    this.openClaw(300);

    const accuracyResult = this.accuracySystem.calculate(this.craneX, this.platform);
    this.state = 'DROP';
    this.dropButton.setVisible(false);
    this.instructionText.setText('Dropping...');

    eventTracker.track('grab_attempt', {
      x: this.craneX,
      targetX: this.currentTargetX,
      accuracy: accuracyResult.accuracy,
      toy: this.currentToy.id,
      rarity: this.currentToy.rarity,
    });

    this.tweens.add({
      targets: this.crane,
      y: this.dropY,
      duration: 800,
      ease: 'Power2',
      onComplete: () => this.onGrab(accuracyResult.accuracy),
    });
  }

  private onGrab(accuracy: number): void {
    this.state = 'GRABBING';
    soundManager.play('clawClose');

    const result = this.grabSystem.attempt(this.currentToy, accuracy);
    const isTargetClose = Math.abs(this.craneX - this.currentTargetX) < 40;

    // Close the claw for a dramatic moment, then decide whether it holds the toy.
    this.closeClaw(500);

    this.time.delayedCall(500, () => {
      if ((result.success || result.nearMiss) && isTargetClose) {
        this.attachToyToClaw();
      }
      this.liftCrane(result);
    });
  }

  private liftCrane(result: { success: boolean; nearMiss: boolean; toy: ToyConfig }): void {
    this.state = 'LIFTING';
    soundManager.play('clawMove');

    this.tweens.add({
      targets: this.crane,
      y: this.topY,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => this.shakeCrane(result),
    });
  }

  private shakeCrane(result: { success: boolean; nearMiss: boolean; toy: ToyConfig }): void {
    this.state = 'SUSPENSE';
    // More suspenseful shake for misses; subtle for guaranteed success.
    const amplitude = result.success ? 3 : 6;

    this.tweens.add({
      targets: this.crane,
      rotation: {
        from: -amplitude * Phaser.Math.DEG_TO_RAD,
        to: amplitude * Phaser.Math.DEG_TO_RAD,
      },
      duration: 200,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.crane.setRotation(0);
        this.resolveResult(result);
      },
    });
  }

  private resolveResult(result: { success: boolean; nearMiss: boolean; toy: ToyConfig }): void {
    const isTargetClose = this.grabbedToy !== null;

    if (result.success && isTargetClose) {
      this.showResult({ success: true, nearMiss: false, toy: result.toy });
      return;
    }

    if (result.nearMiss && isTargetClose) {
      // Drama: it looks like a win, then the toy slips and falls.
      this.openClaw(200);
      soundManager.play('toyDrop');
      const fallingToy = this.grabbedToy;
      this.detachToy();
      if (fallingToy) {
        this.tweens.add({
          targets: fallingToy,
          y: 520,
          duration: 600,
          ease: 'Bounce.easeOut',
          onComplete: () => this.showResult({ success: false, nearMiss: true, toy: result.toy }),
        });
      } else {
        this.showResult({ success: false, nearMiss: true, toy: result.toy });
      }
      return;
    }

    // Plain failure: nothing (or an empty claw) rises back up.
    if (this.grabbedToy) {
      this.openClaw(200);
      soundManager.play('toyDrop');
      this.detachToy();
    }
    this.showResult({ success: false, nearMiss: false, toy: result.toy });
  }

  private attachToyToClaw(): void {
    const targetSprite = this.toySprites.find((s) => Math.abs(s.x - this.currentTargetX) < 40);
    if (!targetSprite) return;

    this.grabbedToy = targetSprite;
    const localPos = this.claw.getLocalPoint(targetSprite.x, targetSprite.y);
    targetSprite.setPosition(localPos.x, localPos.y + 60);
    this.claw.add(targetSprite);
  }

  private detachToy(): void {
    if (!this.grabbedToy) return;
    const transform = this.grabbedToy.getWorldTransformMatrix();
    this.grabbedToy.setPosition(transform.tx, transform.ty);
    this.claw.remove(this.grabbedToy);
    this.add.existing(this.grabbedToy);
    this.grabbedToy.setRotation(0);
  }

  private showResult(result: { success: boolean; nearMiss: boolean; toy: ToyConfig }): void {
    this.state = 'RESULT';

    let isNew = false;
    const { toy } = result;

    if (result.success) {
      soundManager.play('success');
      isNew = this.collectionSystem.unlock(toy);
      if (isNew) soundManager.play('unlock');
      eventTracker.track('collection_add', {
        toy: toy.id,
        rarity: toy.rarity,
        isNew,
      });
    } else if (result.nearMiss) {
      soundManager.play('nearMiss');
    } else {
      soundManager.play('fail');
    }

    eventTracker.track(result.success ? 'grab_success' : result.nearMiss ? 'grab_fail_near_miss' : 'grab_fail', {
      toy: toy.id,
      rarity: toy.rarity,
    });

    if (result.success && toy.rarity === 'legendary') {
      this.scene.start('LegendaryRevealScene', { toy });
      return;
    }

    const type = result.success ? 'success' : result.nearMiss ? 'nearMiss' : 'failure';
    this.resultPanel = new ResultOverlay(
      this,
      type,
      toy,
      isNew,
      () => this.onResultContinue(),
      () => this.onRetry()
    );
    this.add.existing(this.resultPanel);
  }

  private onResultContinue(): void {
    soundManager.play('click');
    transitionManager.go(this, 'CollectionScene');
  }

  private onRetry(): void {
    soundManager.play('retry');
    eventTracker.track('retry_click');
    this.resultPanel?.destroy();
    this.resultPanel = null;
    this.resetRound();
  }

  private resetRound(): void {
    if (this.grabbedToy) {
      this.grabbedToy.destroy();
      this.grabbedToy = null;
    }

    this.spawnSystem.recordResult(false, this.currentToy.rarity);
    this.currentToy = this.spawnSystem.spawn();

    this.crane.setY(this.topY);
    this.crane.setRotation(0);
    this.state = 'IDLE';
    this.dropButton.setVisible(true);
    this.instructionText.setText(
      this.platform === 'desktop' ? 'Click inside the machine to aim • Click DROP to grab!' : 'Tap anywhere when ready!'
    );

    this.addToys();
    this.hudBadgeText.setText(`${this.collectionSystem.count()} Toys`);
  }

  private handleNavigation(key: string): void {
    soundManager.play('click');
    switch (key) {
      case 'collection':
        transitionManager.go(this, 'CollectionScene');
        break;
      case 'settings':
        transitionManager.go(this, 'SettingsScene');
        break;
      case 'analytics':
        transitionManager.go(this, 'AnalyticsDashboardScene');
        break;
      case 'debug':
        transitionManager.go(this, 'DebugPanelScene');
        break;
    }
  }
}
